"use client";

import { useCallback, useEffect, useState } from "react";
import { Mail, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSnack } from "common/hooks";
import { Label } from "@/components/ui/label";
import { Modal } from "components/modal";
import { useSendDevis } from "modules/devis/api/src/devis.api";
import { SendDevisPayload } from "modules/devis/types/devis.interface";

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
  clientName: string;
  clientEmail: string;
  quoteDate: string;
  quoteTotal: string;
  download?: () => Promise<void | Blob>;
  pdfUrl?: string;
  isQuote?: boolean;
}

export function SendEmailModal({
  isOpen,
  onClose,
  quoteId,
  clientName,
  clientEmail,
  quoteDate,
  quoteTotal,
  download,
  isQuote = false,
}: SendEmailModalProps) {
  const snack = useSnack();
  const [email, setEmail] = useState(clientEmail);

  const generateDescription = useCallback(() => {
    return `Bonjour ${clientName},\n\nVeuillez trouver ci-joint notre ${isQuote ? "devis" : "facture"} #${quoteId} daté du ${quoteDate} d'un montant de ${quoteTotal}€.\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement,\nL'équipe Fée Froid`;
  }, [clientName, quoteId, quoteDate, quoteTotal, isQuote]);

  const generateSubject = useCallback(() => {
    return `${isQuote ? "Devis" : "Facture"} #${quoteId} - Fée Froid`;
  }, [quoteId, isQuote]);

  const [subject, setSubject] = useState(generateSubject());
  const [message, setMessage] = useState(generateDescription());

  const { mutate: sendDevis, isLoading } = useSendDevis();

  useEffect(() => {
    setSubject(generateSubject());
    setMessage(generateDescription());
    setEmail(clientEmail);
  }, [
    clientName,
    quoteId,
    quoteDate,
    quoteTotal,
    generateSubject,
    generateDescription,
    clientEmail,
  ]);

  const handleSendEmail = async () => {
    if (!email) {
      snack({
        title: "Veuillez saisir une adresse email valide",
        severityType: "error",
      });
      return;
    }

    try {
      // Send document ID - let backend handle PDF fetching with proper auth
      const payload: SendDevisPayload = {
        to: email,
        subject: subject,
        text: message,
        ...(isQuote 
          ? { quoteId } 
          : { invoiceId: quoteId }
        )
      };

      await sendDevis(payload);
      
      snack({
        title: "Email envoyé avec succès",
        severityType: "success",
      });
      
      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      snack({
        title: "Erreur lors de l'envoi de l'email",
        severityType: "error",
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal>
      <div className="w-full min-w-[275px] sm:w-[500px]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Envoyer {isQuote ? "le devis" : "la facture"} par email
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="text-black h-5 w-5" />
            <span className="sr-only">Fermer</span>
          </Button>
        </div>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="email">Email du destinataire</Label>
            <Input
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Objet</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="hover:bg-gray-200"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={isLoading}
            className="bg-brand-500 text-white hover:bg-brand-600 hover:opacity-50 active:bg-brand-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Envoi En cours...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Envoyer
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
